import { action, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { FormGroup} from 'reactstrap';
import { Checkbox, ChoiceGroup, IChoiceGroupOption } from 'office-ui-fabric-react';

import { PersonLookupModel } from '@app/Models';

import { appStore } from '@app/AppStore';
import UrlService from '@app/Services/UrlService';
import { displayName, isRequired } from '@app/Services/Validation';
import { BaseFormModel } from '@app/Services/BaseFormModel';

import { AzureAuth, UserRoles } from '@app/Components/AzureAuth';
import PersonFilter from '@app/Components/PersonFilter';
import { ModalWindow, ModalDialogOptions, ModalButtonType } from '../Modal';
import { roleMapping } from '@app/AppConstants';

type ImpersonationDialogProps = {
    personId?: string;
    canImpersonateAsAdmin: boolean;
};

@observer
export default class ImpersonationDialog extends React.Component<ImpersonationDialogProps, {}> {
    private _store: ImpersonationStore;
    
    constructor(props: ImpersonationDialogProps){
        super(props);

        const { personId } = props;
        this._store = new ImpersonationStore(props);
        if (personId) {
            this._store.form.personId = personId;
        }
    }

    public getModalOptions(window: ModalWindow<void>): ModalDialogOptions<void> {
        return {
            title: 'Impersonate',
            width: '500px',
            buttons: [{
                type: ModalButtonType.Cancel,
                onClick: () => {
                    window.close();
                }
            }, {
                title: 'Impersonate',
                type: ModalButtonType.Confirm,
                color: 'success',
                isDisabled: !this._store.form.isFormValid,
                onClick: () => {
                    if (this._store.onEnterImpersonation()){
                        window.close(ModalButtonType.Confirm);
                    }
                }
            }]
        };
    }
    
    render() {
        const { form, onSelectPerson, changeImpersonateAsPrincipal, azureRoles } = this._store;
        
        return (
            <>
                <FormGroup>
                    <FormGroup>
                        <PersonFilter
                            title="Person:"
                            size="sm"
                            formModel={form}
                            name="personId"
                            value={form.personId}
                            onSelect={onSelectPerson}
                            isPersonDisabled={(person: PersonLookupModel) => !person.email || !person.email.endsWith('@flyedelweiss.com')}/>
                    </FormGroup>
                </FormGroup>
                <FormGroup>
                    
                    {AzureAuth.isUserInAdminGroup && 
                        <Checkbox name="impersonateAsPrincipal" label="Impersonate as principal" className="impersonate-role"
                            checked={form.impersonateAsPrincipal}
                            onChange={changeImpersonateAsPrincipal}
                        />
                    }

                    {azureRoles.length > 1 && 
                        <>
                            <div className="filters-list-item-title">Roles:</div>
                            <ChoiceGroup
                                options={azureRoles.map(x => {
                                    const substringArray = x.split('.');
                                    const roleValue = substringArray[substringArray.length - 1];
                                    return {
                                        key: x,
                                        text: roleMapping[roleValue] || roleValue
                                    };
                                })}
                                onChange={this._onChange}
                            />
                        </>
                    }

                </FormGroup>
            </>
        );
    }

    @action.bound
    private _onChange(ev?: React.FormEvent<HTMLInputElement | HTMLElement>, option?: IChoiceGroupOption): void {
        if(option) {
            this._store.checkboxHandler(option.key);
        }
    }
}

class FormModel extends BaseFormModel {
    @observable
    @displayName('person')
    personId: string = '';

    @observable
    @isRequired()
    @displayName('role')
    azureRole: string = '';
    
    @observable
    @displayName('impersonateAsPrincipal')
    impersonateAsPrincipal: boolean = false;
}

class ImpersonationStore {
    form: FormModel = new FormModel();
    @observable azureRoles: string[] = [];

    constructor(params: ImpersonationDialogProps) {
        if(params.canImpersonateAsAdmin){
            this.azureRoles.push(UserRoles.administrator);
        }
        this.azureRoles.push(UserRoles.it);
        this.azureRoles.push(UserRoles.support);
        this.azureRoles.push(UserRoles.humanResources);
        this.azureRoles.push(UserRoles.cockpitPlanning);
        this.azureRoles.push(UserRoles.cabinPlanning);
        this.azureRoles.push(UserRoles.crewOffice);
        this.azureRoles.push(UserRoles.cockpitLine);
        this.azureRoles.push(UserRoles.cabinLine);
        this.azureRoles.push(UserRoles.cockpitTraining);
        this.azureRoles.push(UserRoles.cabinTraining);
        this.azureRoles.push(UserRoles.marketing);
        this.azureRoles.push(UserRoles.crew);
    }

    @action.bound
    onSelectPerson(value: string){
        this.form.personId = value;
        this.form.name = name;
    }

    @action.bound
    changeImpersonateAsPrincipal(){
        this.form.impersonateAsPrincipal = !this.form.impersonateAsPrincipal;
    }

    @action.bound
    checkboxHandler(azureRole: string){
        this.form.azureRole = azureRole;
    }
    
    @action.bound
    onEnterImpersonation() {
        if (!this.form.validate()) {
            return false;
        }

        const data = {
            personId: this.form.personId,
            role: this.form.azureRole,
            impersonateAsPrincipal: this.form.impersonateAsPrincipal
        };
        appStore.setValue('impersonatedUser', data);
        UrlService.clearParams();
        location.reload();
        return true;
    }
}